/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { type Registry as Services, service } from '@ember/service';
import { action } from '@ember/object';
import { waitFor } from '@ember/test-waiters';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import { AnalyticsEvents } from 'uplisting-frontend/services/analytics';

interface IArgs {
  booking: ModelRegistry['booking'];
  saveOnSelect?: false;
}

interface BookingPropertySelectorSignature {
  Element: null;

  Args: IArgs;
}

export default class UiBookingPropertySelectorComponent extends Component<BookingPropertySelectorSignature> {
  @service analytics!: Services['analytics'];
  @service notifications!: Services['notifications'];

  @service('repositories/property')
  propertyRepository!: Services['repositories/property'];

  @service('calendar/manager')
  calendarManager!: Services['calendar/manager'];

  @tracked showConfirmModal = false;

  @cached
  get booking(): ModelRegistry['booking'] {
    return this.args.booking;
  }

  @cached
  get saveOnSelect(): boolean {
    return this.args.saveOnSelect ?? true;
  }

  @cached
  get properties(): ModelRegistry['property'][] {
    return this.propertyRepository.peekAll();
  }

  @cached
  get hasOutstandingSplitPayments(): boolean {
    return !!this.booking.bookingPayment?.hasOutstandingSplitPayments;
  }

  @cached
  get stripeConnection(): ModelRegistry['stripe-connection'] | undefined {
    return this.booking.property.stripeConnection;
  }

  @cached
  get destinationProperties(): ModelRegistry['property'][] {
    const { properties, stripeConnection } = this;

    if (!this.hasOutstandingSplitPayments || !stripeConnection?.properties) {
      return properties;
    }

    const ids = stripeConnection.hasMany('properties').ids();

    return properties.filter((property) => ids.includes(property.id));
  }

  @action
  @waitFor
  async handleInsert(): Promise<void> {
    await this.propertyRepository.findRecord(this.booking.property.id);
  }

  @action
  handleChange(property: ModelRegistry['property']): void {
    this.booking.newProperty = property;

    if (this.booking.newProperty === this.booking.property) {
      this.booking.newProperty = undefined;
    }

    if (this.saveOnSelect) {
      this.showConfirmModal = true;
    }
  }

  @action
  handleCancel(): void {
    if (!this.booking.isSaving) {
      this.booking.newProperty = undefined;
    }

    this.showConfirmModal = false;
  }

  @action
  @waitFor
  async handleSubmit(): Promise<void> {
    try {
      await this.booking.save();

      this.analytics.trackEvent(AnalyticsEvents.bookingMoved);
      this.notifications.info(
        'action_bookings_summary.change_property.notifications.applied',
      );

      this.booking.set('newProperty', undefined);
      this.calendarManager.data.setupFasterLookup();
    } catch {
      this.notifications.error();
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::Booking::PropertySelector': typeof UiBookingPropertySelectorComponent;
  }
}
